/**
 * Minimal JWT helper. We only read the unverified payload client-side to
 * schedule a session-expiry timer; the server is the source of truth on
 * actual validity. Verifying signatures client-side would require shipping
 * the secret, which we don't.
 */

export interface JwtPayload {
  exp?: number;
  iat?: number;
  sub?: string;
  [key: string]: unknown;
}

export function decodeJwt(token: string | null | undefined): JwtPayload | null {
  if (!token) return null;
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const normalised = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalised + '='.repeat((4 - (normalised.length % 4)) % 4);
    return JSON.parse(atob(padded)) as JwtPayload;
  } catch {
    return null;
  }
}

/**
 * ATL-001144 — server-clock offset (server time minus client time), learned
 * from the `iat` of a token the server has just issued. Expiry is judged on
 * the SERVER's clock: with 15-minute tokens, a client clock that runs a few
 * minutes fast would otherwise see every new token as already expired and
 * refresh in a loop. Persisted so a reload keeps it; self-corrects on the
 * next freshly issued token.
 */
const CLOCK_OFFSET_KEY = 'waypoint:server-clock-offset-ms';
let clockOffsetMs = readClockOffset();

function readClockOffset(): number {
  try {
    const n = Number(localStorage.getItem(CLOCK_OFFSET_KEY));
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

/** Call with a token the server issued moment ago (login / refresh response). */
export function recordServerClock(freshToken: string | null | undefined): void {
  const iat = decodeJwt(freshToken)?.iat;
  if (!iat) return;
  clockOffsetMs = iat * 1000 - Date.now();
  try {
    localStorage.setItem(CLOCK_OFFSET_KEY, String(clockOffsetMs));
  } catch {
    /* storage unavailable — the in-memory offset still applies */
  }
}

/** Milliseconds until the token expires (server clock). Negative = already expired. Null = no/invalid exp. */
export function msUntilExpiry(token: string | null | undefined): number | null {
  const payload = decodeJwt(token);
  if (!payload?.exp) return null;
  return payload.exp * 1000 - (Date.now() + clockOffsetMs);
}

export function isExpired(token: string | null | undefined): boolean {
  const remaining = msUntilExpiry(token);
  return remaining !== null && remaining <= 0;
}
